import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';
import '../../styles/modal.css';
import '../../styles/version-history.css';
import { formatDateTime } from '../../utils/dateTimeFormat';

const MAX_NAME_CHARS = 16;

const truncateByChars = (text = '', maxChars = MAX_NAME_CHARS) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { display: '', full: '', isTruncated: false };

  if (trimmed.length <= maxChars) {
    return { display: trimmed, full: trimmed, isTruncated: false };
  }

  return {
    display: `${trimmed.slice(0, maxChars)}...`,
    full: trimmed,
    isTruncated: true,
  };
};

const TestCaseReviewPanel = ({ isOpen, onClose, testCase, onSubmitReview }) => {
  // Loading states for different operations
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  const [loadingReviewStatus, setLoadingReviewStatus] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  
  // Data states
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [comments, setComments] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [existingReviews, setExistingReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('assign');
  
  // Error states
  const [error, setError] = useState('');

  // Get user details from localStorage
  const user = JSON.parse(localStorage.getItem('user'));
  const user_type = user?.user_type;
  const user_id = user?.userId;
  
  // Check if user can assign reviewers (only user_type 1 and 2)
  const canAssignReviewers = user_type === 1 || user_type === 2;
  // API: Fetch available reviewers for test case
  const fetchAvailableReviewers = useCallback(async () => {
    if (!testCase?.id) return;
    
    // Only fetch reviewers if user has permission to assign reviewers
    if (!canAssignReviewers) {
      console.warn('User does not have permission to fetch reviewers');
      return;
    }
    
    setLoadingReviewers(true);
    setError('');
    
    try {
      const response = await axiosInstance.get(`test_review/list_available_reviewers?test_case_id=${testCase.id}`);
      
      if (response.status_code === 200) {
        const formattedReviewers = response.data.map(user => ({
          id: user.user_id,
          name: `${user.first_name} ${user.last_name}`.trim(),
          email: user.email,
          role: user.job_title || 'Team Member',
          avatar: user.first_name?.charAt(0)?.toUpperCase() || '👤'
        }));
        setReviewers(formattedReviewers);
      } else {
        setError('Failed to fetch reviewers');
      }
    } catch (err) {
      console.error('Error fetching reviewers:', err);
      setError('Failed to load reviewers. Please try again.');
    } finally {
      setLoadingReviewers(false);
    }
  }, [testCase?.id, canAssignReviewers]);

  // API: Assign reviewers to test case
  const assignReviewers = async () => {
    if (selectedReviewers.length === 0) {
      setError('Please select at least one reviewer');
      return;
    }
    
    setSubmittingReview(true);
    setError('');
    
    try {
      const response = await axiosInstance.post('test_review/assign_test_reviewers', {
        test_case_id: testCase.id,
        user_ids: selectedReviewers
      });
      
      if (response.status_code === 200) {
        // Success notification
        if (onSubmitReview) {
          onSubmitReview({
            action: 'assign',
            reviewers: selectedReviewers.map(id => reviewers.find(r => r.id === id)),
            testCaseId: testCase.id,
            message: response.message
          });
        }
        onClose();
      } else {
        setError('Failed to assign reviewers');
      }
    } catch (err) {
      console.error('Error assigning reviewers:', err);
      setError('Failed to assign reviewers. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // API: Fetch current user's review status for the test case
  const fetchReviewStatus = useCallback(async () => {
    if (!testCase?.id) return;
    
    setLoadingReviewStatus(true);
    setError('');
    
    try {
      const response = await axiosInstance.get(`test_review/get_review_status?test_case_id=${testCase.id}`);
      
      if (response.status_code === 200) {
        const reviewData = response.data;
        setComments(reviewData.comments || '');
        setReviewStatus(reviewData.review_status.toString());
      }
    } catch (err) {
      // 404 means no existing review - reset to defaults
      if (err.response?.status === 404) {
        setComments('');
        setReviewStatus('');
      } else {
        console.error('Error fetching review status:', err);
        setError('Failed to load review status');
      }
    } finally {
      setLoadingReviewStatus(false);
    }
  }, [testCase?.id]);

  // API: Submit/update review
  const submitReview = async () => {
    if (!comments.trim()) {
      setError('Please add comments for your review');
      return;
    }
    
    if (reviewStatus === '') {
      setError('Please select a review status');
      return;
    }
    
    setSubmittingReview(true);
    setError('');
    
    try {
      const response = await axiosInstance.put('test_review/update_review_status', {
        test_case_id: testCase.id,
        review_status: parseInt(reviewStatus),
        comments: comments.trim()
      });
      
      if (response.status_code === 200) {
        // Success notification
        if (onSubmitReview) {
          onSubmitReview({
            action: 'review',
            status: getStatusText(parseInt(reviewStatus)),
            comments: comments.trim(),
            testCaseId: testCase.id,
            message: response.message
          });
        }
        onClose();
      } else {
        setError('Failed to submit review');
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // API: Fetch review history for the test case
  const fetchReviewHistory = useCallback(async () => {
    if (!testCase?.id) return;
    
    setLoadingHistory(true);
    setError('');
    
    try {
      const response = await axiosInstance.get(`test_review/test_review_history?test_case_id=${testCase.id}`);
      
      if (response.status_code === 200) {
        const formattedReviews = response.data.map((review, index) => ({
          id: index + 1,
          reviewerName: review.reviewer_name,
          reviewerAvatar: review.reviewer_name?.charAt(0)?.toUpperCase() || '�',
          status: getStatusText(review.review_status).toLowerCase(),
          comments: review.comments,
          date: review.updated_at,
          version: testCase.version || '1.0'
        }));
        setExistingReviews(formattedReviews);
      } else {
        setError('Failed to fetch review history');
      }
    } catch (err) {
      console.error('Error fetching review history:', err);
      setError('Failed to load review history');
    } finally {
      setLoadingHistory(false);
    }
  }, [testCase?.id, testCase?.version]);

  // Helper function to convert status number to text
  const getStatusText = (statusCode) => {
    switch (statusCode) {
      case 0: return 'Pending';
      case 1: return 'Approved';
      case 2: return 'Rejected';
      default: return 'Unknown';
    }
  };

  // Set default tab based on user permissions
  useEffect(() => {
    if (!canAssignReviewers && activeTab === 'assign') {
      setActiveTab('review');
    }
  }, [canAssignReviewers, activeTab]);

  // Reset state when modal opens/closes and load initial tab data
  useEffect(() => {
    if (isOpen && testCase) {
      setError('');
      setSelectedReviewers([]);
      
      // Load initial data for the default tab (only if user can assign reviewers)
      if (activeTab === 'assign' && canAssignReviewers) {
        fetchAvailableReviewers();
      }
    }
  }, [isOpen, testCase, fetchAvailableReviewers, canAssignReviewers]);

  // Handle tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    
    // Load data for the selected tab (only when switching tabs, not on initial load)
    if (tab === 'assign' && reviewers.length === 0 && canAssignReviewers) {
      fetchAvailableReviewers();
    } else if (tab === 'review') {
      fetchReviewStatus();
    } else if (tab === 'history') {
      fetchReviewHistory();
    }
  };

  const handleReviewerSelection = (reviewerId) => {
    setSelectedReviewers(prev => {
      if (prev.includes(reviewerId)) {
        return prev.filter(id => id !== reviewerId);
      } else {
        return [...prev, reviewerId];
      }
    });
    setError(''); // Clear error when user makes a selection
  };

  const handleSubmitReview = () => {
    if (activeTab === 'assign') {
      assignReviewers();
    } else if (activeTab === 'review') {
      submitReview();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#52c41a';
      case 'rejected':
        return '#f5222d';
      case 'pending':
        return '#faad14';
      default:
        return '#1890ff';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      case 'pending':
        return '⏳';
      default:
        return '🔄';
    }
  };

  // Loading component
  const LoadingSpinner = ({ text = "Loading..." }) => (
    <div className="loading-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
      <div className="loading-spinner" style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
      <p style={{ marginTop: "16px", color: "#666" }}>{text}</p>
    </div>
  );

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div style={{ padding: "20px", textAlign: "center", color: "#f5222d", backgroundColor: "#fff2f0", borderRadius: "8px", margin: "16px 0" }}>
      <p style={{ margin: "0 0 12px 0" }}>{message}</p>
      {onRetry && (
        <button 
          onClick={onRetry}
          style={{ 
            padding: "6px 12px", 
            backgroundColor: "#f5222d", 
            color: "white", 
            border: "none", 
            borderRadius: "4px", 
            cursor: "pointer",
            fontSize: "0.9rem"
          }}
        >
          Retry
        </button>
      )}
    </div>
  );

  if (!isOpen) return null;
  const truncatedTestCaseName = truncateByChars(testCase?.name);

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay" onClick={onClose} style={{zIndex:14000}}></div>
      <div className="modal review-panel-modal" style={{ maxWidth: "800px", width: "90%", maxHeight: "90vh", borderRadius: "8px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)" ,zIndex:14500}}>
        <div className="modal-header" style={{ borderBottom: "1px solid #e9ecef", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: "0", fontSize: "1.4rem", fontWeight: "600", color: "#212529" }}>
            {activeTab === 'assign' && canAssignReviewers ? 'Assign Reviewers' : 'Review Test Case'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="review-tabs" style={{ display: "flex", borderBottom: "1px solid #e9ecef", padding: "0 24px", backgroundColor: "#f8f9fa" }}>
          {canAssignReviewers && (
            <button
              className={`tab-button ${activeTab === 'assign' ? 'active' : ''}`}
              onClick={() => handleTabChange('assign')}
              style={{
                padding: "12px 24px",
                background: "none",
                border: "none",
                borderBottom: activeTab === 'assign' ? "2px solid #007bff" : "2px solid transparent",
                fontSize: "0.9rem",
                fontWeight: "500",
                color: activeTab === 'assign' ? "#007bff" : "#6c757d",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
            >
              Assign Reviewers
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
            onClick={() => handleTabChange('review')}
            style={{
              padding: "12px 24px",
              background: "none",
              border: "none",
              borderBottom: activeTab === 'review' ? "2px solid #007bff" : "2px solid transparent",
              fontSize: "0.9rem",
              fontWeight: "500",
              color: activeTab === 'review' ? "#007bff" : "#6c757d",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            Add Review
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => handleTabChange('history')}
            style={{
              padding: "12px 24px",
              background: "none",
              border: "none",
              borderBottom: activeTab === 'history' ? "2px solid #007bff" : "2px solid transparent",
              fontSize: "0.9rem",
              fontWeight: "500",
              color: activeTab === 'history' ? "#007bff" : "#6c757d",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            Review History
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: "24px", maxHeight: "60vh", overflowY: "auto" }}>
          {/* Display error message if any */}
          {error && (
            <ErrorMessage 
              message={error} 
              onRetry={() => {
                if (activeTab === 'assign') fetchAvailableReviewers();
                else if (activeTab === 'review') fetchReviewStatus();
                else if (activeTab === 'history') fetchReviewHistory();
              }} 
            />
          )}
          
          {activeTab === 'assign' && canAssignReviewers && (
            <div className="assign-reviewers-tab">
              <div className="test-case-info" style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1rem" }}>
                  Test Case:{' '}
                  <span title={truncatedTestCaseName.isTruncated ? truncatedTestCaseName.full : undefined}>
                    {truncatedTestCaseName.display}
                  </span>
                </h3>
                <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#666" }}>
                  Select reviewers to evaluate this test case. They will receive a notification to review the test case.
                </p>
              </div>
              
              {loadingReviewers ? (
                <LoadingSpinner text="Loading reviewers..." />
              ) : (
                <div className="reviewers-list" style={{ marginBottom: "24px" }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: "1.1rem" }}>Available Reviewers</h3>
                  
                  {reviewers.length > 0 ? (
                    reviewers.map(reviewer => (
                      <div 
                        key={reviewer.id} 
                        className={`reviewer-item ${selectedReviewers.includes(reviewer.id) ? 'selected' : ''}`}
                        style={{
                          padding: "16px",
                          marginBottom: "12px",
                          borderRadius: "8px",
                          border: "1px solid #eee",
                          backgroundColor: selectedReviewers.includes(reviewer.id) ? "#e6f7ff" : "white",
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => handleReviewerSelection(reviewer.id)}
                      >
                        <div className="reviewer-avatar" style={{ fontSize: "1.5rem", marginRight: "16px" }}>
                          {reviewer.avatar}
                        </div>
                        <div className="reviewer-info" style={{ flex: "1", gap : "5px"}}>
                          <div className="reviewer-name" style={{ fontWeight: "500", fontSize: "1rem" }}>{reviewer.name}</div>
                          <div className="reviewer-role" style={{ fontSize: "0.8rem", color: "#666" }}>({reviewer.role})</div>
                          <div className="reviewer-email" style={{ fontSize: "0.8rem", color: "#666" }}>({reviewer.email})</div>
                        </div>
                        <div className="reviewer-select">
                          <input 
                            type="checkbox" 
                            checked={selectedReviewers.includes(reviewer.id)} 
                            onChange={() => {}} 
                            style={{ width: "18px", height: "18px" }}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state" style={{ textAlign: "center", padding: "24px", color: "#666" }}>
                      <p>No reviewers available.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {activeTab === 'review' && (
            <div className="add-review-tab">
              <div className="test-case-info" style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1rem" }}>
                  Test Case:{' '}
                  <span title={truncatedTestCaseName.isTruncated ? truncatedTestCaseName.full : undefined}>
                    {truncatedTestCaseName.display}
                  </span>
                </h3>
                <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#666" }}>
                  Add your review for this test case. Your feedback will help improve the quality of the test.
                </p>
              </div>
              
              {loadingReviewStatus ? (
                <LoadingSpinner text="Loading review status..." />
              ) : (
                <div className="review-form" style={{ marginBottom: "24px" }}>
                  <div className="form-group" style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>Review Status:</label>
                    <div style={{ display: "flex", gap: "16px" }}>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <input 
                          type="radio" 
                          name="reviewStatus" 
                          value="1" 
                          checked={reviewStatus === '1'} 
                          onChange={() => {setReviewStatus('1'); setError('');}}
                          style={{ marginRight: "8px" }}
                        />
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ marginRight: "4px" }}>✅</span> Approve
                        </span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <input 
                          type="radio" 
                          name="reviewStatus" 
                          value="2" 
                          checked={reviewStatus === '2'} 
                          onChange={() => {setReviewStatus('2'); setError('');}}
                          style={{ marginRight: "8px" }}
                        />
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ marginRight: "4px" }}>❌</span> Reject
                        </span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <input 
                          type="radio" 
                          name="reviewStatus" 
                          value="0" 
                          checked={reviewStatus === '0'} 
                          onChange={() => {setReviewStatus('0'); setError('');}}
                          style={{ marginRight: "8px" }}
                        />
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ marginRight: "4px" }}>⏳</span> Pending
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>Comments:</label>
                    <textarea 
                      value={comments}
                      onChange={(e) => {setComments(e.target.value); setError('');}}
                      placeholder="Add your comments, suggestions, or feedback about this test case..."
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        minHeight: "120px",
                        fontSize: "0.9rem"
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'history' && (
            <div className="review-history-tab">
              <div className="test-case-info" style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1rem" }}>
                  Test Case:{' '}
                  <span title={truncatedTestCaseName.isTruncated ? truncatedTestCaseName.full : undefined}>
                    {truncatedTestCaseName.display}
                  </span>
                </h3>
                <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem", color: "#666" }}>
                  Review history for this test case. See all previous reviews and their status.
                </p>
              </div>
              
              {loadingHistory ? (
                <LoadingSpinner text="Loading review history..." />
              ) : (
                <div className="reviews-list" style={{ marginBottom: "24px" }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: "1.1rem" }}>Previous Reviews</h3>
                  
                  {existingReviews.length > 0 ? (
                    existingReviews.map(review => (
                      <div 
                        key={review.id} 
                        className="review-item"
                        style={{
                          padding: "16px",
                          marginBottom: "16px",
                          borderRadius: "8px",
                          border: "1px solid #eee",
                          backgroundColor: "white",
                          borderLeft: `4px solid ${getStatusColor(review.status)}`
                        }}
                      >
                        <div className="review-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                          <div className="reviewer-info" style={{ display: "flex", alignItems: "center" }}>
                            <div className="reviewer-avatar" style={{ fontSize: "1.2rem", marginRight: "8px" }}>
                              {review.reviewerAvatar}
                            </div>
                            <div>
                              <div className="reviewer-name" style={{ fontWeight: "500" }}>{review.reviewerName}</div>
                              <div className="review-date" style={{ fontSize: "0.8rem", color: "#666" }}>
                                {formatDateTime(review.date)} • Version {review.version}
                              </div>
                            </div>
                          </div>
                          <div 
                            className="review-status"
                            style={{
                              padding: "4px 12px",
                              borderRadius: "12px",
                              backgroundColor: `${getStatusColor(review.status)}20`,
                              color: getStatusColor(review.status),
                              fontWeight: "500",
                              fontSize: "0.8rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px"
                            }}
                          >
                            <span>{getStatusIcon(review.status)}</span>
                            <span>{review.status.charAt(0).toUpperCase() + review.status.slice(1)}</span>
                          </div>
                        </div>
                        
                        <div className="review-comments" style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>
                          {review.comments}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state" style={{ textAlign: "center", padding: "24px", color: "#666" }}>
                      <p>No reviews have been submitted yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="modal-footer" style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "16px 24px",
          borderTop: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa",
          gap: "12px"
        }}>
          <button 
            className="btn btn-outline" 
            style={{ 
              padding: "8px 16px", 
              backgroundColor: "white", 
              border: "1px solid #ccc", 
              borderRadius: "4px", 
              cursor: "pointer" 
            }} 
            onClick={onClose}
            disabled={submittingReview}
          >
            Cancel
          </button>
          
          {((activeTab === 'assign' && canAssignReviewers) || activeTab === 'review') && (
            <button 
              className="btn btn-primary" 
              style={{ 
                padding: "8px 16px", 
                backgroundColor: submittingReview ? "#ccc" : "#007bff", 
                color: "white",
                border: "none", 
                borderRadius: "4px", 
                cursor: submittingReview ? "not-allowed" : "pointer"
              }} 
              onClick={handleSubmitReview}
              disabled={submittingReview}
            >
              {submittingReview ? 'Processing...' : (activeTab === 'assign' ? 'Assign Reviewers' : 'Submit Review')}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default TestCaseReviewPanel;